import { createTheme } from "@mui/material/styles";
import { getCurrentBrand } from "@/config/brands";
import { darkenHex, lightenHex } from "@/utils/colorUtils";

/**
 * Creates a MUI theme based on the current brand configuration
 * @param {string} mode - Theme mode: 'light' or 'dark'
 * @returns {Theme} MUI theme object
 */
export function createAppTheme(mode = "light") {
  const brand = getCurrentBrand();
  const primaryColor = brand.primaryColor;
  const primaryColorLight = lightenHex(primaryColor, 20);
  const primaryColorDark = darkenHex(primaryColor, 5);
  const secondaryColor = brand.secondaryColor;
  const secondaryColorLight = lightenHex(secondaryColor, 20);
  const secondaryColorDark = darkenHex(secondaryColor, 20);
  const borderRadius = brand.borderRadius;
  const buttonBorderRadius = brand.buttonBorderRadius;

  return createTheme({
    palette: {
      mode,
      background: {
        default: mode === "dark" ? "#121212" : brand.formLayoutBackgroundColor,
        paper: mode === "dark" ? "#1e1e1e" : "#ffffff",
      },
      primary: {
        main: primaryColor,
        light: primaryColorLight,
        dark: primaryColorDark,
        contrastText: brand.white,
      },
      secondary: {
        main: secondaryColor,
        light: secondaryColorLight,
        dark: secondaryColorDark,
      },
      header: {
        main: brand.headerPrimaryColor,
      },
      navigation: {
        main: brand.headerNavigationPrimaryColor,
        hover: brand.headerNavigationAccentColor,
      },
      footer: {
        main: brand.footerPrimaryColor,
        accent: brand.footerAccentColor,
        contrastText: brand.white,
      },
      white: {
        main: "#ffffff",
      },
      black: {
        main: "#000000",
      },
      red: {
        main: "#ff0000",
      },
    },
    typography: {
      fontFamily: brand.fontFamily,
    },
    components: {
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: primaryColorDark,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&.MuiTableRow-hover:hover": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : primaryColorLight,
              "& .MuiTableCell-root": {
                color: mode === "dark" ? "#ffffff" : "#ffffff",
              },
            },
            "&.Mui-selected": {
              backgroundColor:
                mode === "dark" ? "rgba(255, 255, 255, 0.1)" : primaryColor,
              "& .MuiTableCell-root": {
                color: "#ffffff",
              },
              "&:hover": {
                backgroundColor:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : primaryColorLight,
                "& .MuiTableCell-root": {
                  color: "#ffffff",
                },
              },
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              backgroundColor: mode === "dark" ? "#2d2d2d" : "#dedede",
              color: mode === "dark" ? "#ffffff" : "black",
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            boxShadow: "none",
            borderRadius: borderRadius,
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius,
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor:
              mode === "dark" ? "#121212" : brand.formLayoutBackgroundColor,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "dark" ? "#1e1e1e" : "white",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            color: brand.white,
            fontWeight: "600",
            boxShadow: "none",
            borderRadius: buttonBorderRadius,
            // fontSize and padding removed to allow size prop to work properly
          },
          containedSecondary: {
            color: brand.white,
            fontWeight: "700",
            boxShadow: "none",
            borderRadius: buttonBorderRadius,
            // fontSize and padding removed to allow size prop to work properly
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            borderRadius: borderRadius,
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {},
          content: {
            margin: "0 !important",
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius,
          },
        },
      },
      MuiPickersLayout: {
        styleOverrides: {
          root: {
            border: "2px solid",
            borderColor: brand.primaryColor,
            borderRadius: borderRadius,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            border: "1px solid",
            borderColor: brand.primaryColor,
            borderRadius: borderRadius,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.3)",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {},
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: primaryColor,
              color: brand.toggleButtonGroupTextColor,
              "&:hover": {
                backgroundColor: primaryColorDark,
              },
            },
            "&:not(.Mui-selected)": {
              backgroundColor: mode === "dark" ? "#2d2d2d" : brand.white,
            },
          },
        },
      },
      MuiCardHeader: {
        defaultProps: {
          titleTypographyProps: {
            variant: "h6",
          },
        },
        styleOverrides: {
          root: {
            paddingBottom: 0,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            "&.edit-icon-button": {
              color:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.7)"
                  : primaryColor,
              "&:hover": {
                backgroundColor:
                  mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : primaryColor,
                color: "#ffffff",
                "& .MuiSvgIcon-root": {
                  color: "#ffffff",
                },
              },
            },
          },
        },
      },
    },
  });
}
